import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Trash } from '@phosphor-icons/react';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';

const PERSONS = ['Ladys', 'Vee', 'Norbi', 'Bösch'];

// Admin-only: enter / remove approved holidays (Ferien) for a day.
export const VacationDialog = ({ month, day, vacations, onClose, onSaved }) => {
  const { API, language } = useAuth();
  const L = (en, de) => (language === 'de' ? de : en);
  const [person, setPerson] = useState('Vee');
  const [from, setFrom] = useState(day);
  const [to, setTo] = useState(day);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setFrom(day); setTo(day); setNote(''); }, [day]);
  if (!day) return null;
  const onDay = vacations.filter(v => v.start_date <= day && v.end_date >= day);

  const save = async () => {
    setBusy(true);
    try {
      await API.post(`/work-plans/${month}/vacations`, { person, start_date: from, end_date: to, note: note || null });
      toast.success(L('Holiday entered', 'Ferien eingetragen'));
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || L('Error', 'Fehler')); }
    finally { setBusy(false); }
  };
  const remove = async (v) => {
    if (!window.confirm(L(`Delete holiday of ${v.person}?`, `Ferien von ${v.person} löschen?`))) return;
    try { await API.post(`/work-plans/${month}/vacations/delete`, { id: v.id }); toast.success(L('Deleted', 'Gelöscht')); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || L('Error', 'Fehler')); }
  };

  return (
    <Dialog open={!!day} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-sm" data-testid="vacation-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">{L('Holidays', 'Ferien')}</DialogTitle>
          <DialogDescription>
            {new Date(`${day}T00:00:00`).toLocaleDateString('de-CH', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' })}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {onDay.map(v => (
            <div key={v.id} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2 text-sm" data-testid={`vacation-item-${v.id}`}>
              <div>
                <div className="font-semibold text-slate-800">{v.person}</div>
                <div className="text-xs text-slate-500">
                  {new Date(`${v.start_date}T00:00:00`).toLocaleDateString('de-CH')} – {new Date(`${v.end_date}T00:00:00`).toLocaleDateString('de-CH')}
                  {v.note ? ` · ${v.note}` : ''}
                </div>
              </div>
              <button type="button" onClick={() => remove(v)} className="text-slate-400 hover:text-red-600" data-testid={`vacation-delete-${v.id}`}>
                <Trash size={16} />
              </button>
            </div>
          ))}
          <div className="grid grid-cols-4 gap-2">
            {PERSONS.map(p => (
              <button key={p} type="button" onClick={() => setPerson(p)} data-testid={`vacation-person-${p}`}
                className={`px-2 py-2 rounded-lg text-sm font-semibold border transition-colors ${person === p ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'}`}>
                {p}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs font-semibold text-slate-500 space-y-1">
              <span>{L('From', 'Von')}</span>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="vacation-from" />
            </label>
            <label className="text-xs font-semibold text-slate-500 space-y-1">
              <span>{L('To', 'Bis')}</span>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="vacation-to" />
            </label>
          </div>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={L('Note (optional)', 'Notiz (optional)')} data-testid="vacation-note" />
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} className="flex-1">{L('Cancel', 'Abbrechen')}</Button>
            <Button onClick={save} disabled={busy} className="flex-1 bg-slate-900 text-white hover:bg-slate-700" data-testid="vacation-save-btn">
              {L('Approve & save', 'Genehmigen & speichern')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
